import {
  AddCommentData,
  AddCommentVars,
  CommentData,
  UpdateCommentData,
  UpdateCommentVars
} from '../gql-types';
import { ApolloClient, DataProxy, NormalizedCacheObject, useMutation, useQuery } from '@apollo/client';

import ApolloClientService from '../../shared/services/ApolloClientService';
import Config from '../../shared/config';
import { ExecutionResult } from 'graphql';
import GraphQL from '../gql';
import { cloneDeep } from 'lodash';

let AssessmentApolloClient: ApolloClient<NormalizedCacheObject> = null;

export default function useComments(
  userkey: number,
  modulekey: number,
  layer: number
) {
  if (!AssessmentApolloClient) {
    AssessmentApolloClient = ApolloClientService.create(Config.ASSESSMENT_GQL);
  }

  const {
    loading: loading,
    error: error,
    data: commentData
  } = useQuery<CommentData>(GraphQL.Query.COMMENTS, {
    client: AssessmentApolloClient,
    variables: {
      userkey,
      modulekey,
      layer
    },
    fetchPolicy: 'network-only'
  });

  const [
    _addComment,
    { loading: adding, error: addError, data: addCommentData }
  ] = useMutation<AddCommentData, AddCommentVars>(
    GraphQL.Mutation.CREATE_COMMENT,
    {
      client: AssessmentApolloClient,
      update: (cache: DataProxy, { data }: ExecutionResult<AddCommentData>) => {
        const existingComments = cache.readQuery<CommentData>({
          query: GraphQL.Query.COMMENTS,
          variables: {
            userkey,
            modulekey,
            layer
          }
        });

        const updatedComments = cloneDeep(existingComments.comments);
        updatedComments.push(data.createComment);
        cache.writeQuery<CommentData>({
          query: GraphQL.Query.COMMENTS,
          variables: {
            userkey,
            modulekey,
            layer
          },
          data: {
            comments: updatedComments
          }
        });
      }
    }
  );

  const [
    _updateComment,
    { loading: updating, error: updateError, data: updateCommentData }
  ] = useMutation<UpdateCommentData, UpdateCommentVars>(
    GraphQL.Mutation.UPDATE_COMMENT,
    {
      client: AssessmentApolloClient,
      update: (
        cache: DataProxy,
        { data }: ExecutionResult<UpdateCommentData>
      ) => {
        const newComment = data.updateComment;
        const existingComments = cache.readQuery<CommentData>({
          query: GraphQL.Query.COMMENTS,
          variables: {
            userkey,
            modulekey,
            layer
          }
        });

        const updatedComments = cloneDeep(existingComments.comments);
        const comment = updatedComments.find(
          (c) => c.commentkey === newComment.commentkey
        );
        if (existingComments && newComment) {
          comment.assets.text = newComment.assets.text;
          comment.active = newComment.active;
        }

        cache.writeQuery<CommentData>({
          query: GraphQL.Query.COMMENTS,
          variables: {
            userkey,
            modulekey,
            layer
          },
          data: {
            //Need to filter the deleted comment from the list
            comments: updatedComments.filter((comment) => comment.active)
          }
        });
      }
    }
  );

  function addComment(text: string) {
    _addComment({
      variables: { userkey, modulekey, layer, text }
    });
  }

  function updateComment(
    discussionkey: number,
    commentkey: number,
    text: string,
    active: boolean = true
  ) {
    _updateComment({
      variables: { discussionkey, commentkey, text, active }
    });
  }

  return {
    loading,
    comments: commentData?.comments,
    adding,
    addError,
    addCommentData,
    addComment,
    updateComment
  };
}
